import { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Button,
  Stack,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import KeyIcon from '@mui/icons-material/VpnKey';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

/**
 * The ingest endpoint the SDK POSTs to, resolved for the platform the dashboard
 * is actually served from — so the generated snippet is correct on local dev
 * and on any deployment without manual configuration:
 *   1. an explicit VITE_INGEST_ENDPOINT build override always wins;
 *   2. on localhost, the collector runs on its own port (:4100);
 *   3. otherwise (AWS / any host) ingest is proxied on the same origin (:80/:443),
 *      so the endpoint is just this page's origin.
 */
function resolveIngestEndpoint(): string {
  const override = import.meta.env.VITE_INGEST_ENDPOINT as string | undefined;
  if (override) return override;
  if (typeof window !== 'undefined') {
    const { hostname, origin } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') return 'http://localhost:4100';
    return origin;
  }
  return 'http://localhost:4100';
}

const INGEST_ENDPOINT = resolveIngestEndpoint();

/**
 * URL of the self-contained browser SDK bundle for the <script> install. It is
 * served same-origin by the dashboard (nginx in prod, vite public/ in dev), so
 * the snippet needs no external CDN and works on any deployment. Overridable via
 * VITE_SDK_URL if you host the bundle elsewhere.
 */
function resolveSdkUrl(): string {
  const override = import.meta.env.VITE_SDK_URL as string | undefined;
  if (override) return override;
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  return `${base}/sdk/v1/replayops.min.js`;
}

const SDK_URL = resolveSdkUrl();

/**
 * How wide the card gets. Shared by both states — the one offering to mint a
 * key and the one showing the snippets — because a card that changes width
 * when you press a button reads as a layout bug.
 *
 * Wide enough that a line of the HTML snippet does not wrap: those lines are
 * what the reader is copying, and a wrapped one is harder to trust.
 */
const CARD_MAX_WIDTH = 990;

interface Props {
  projectId: string;
  /** Freshly-created raw key, if one is available; otherwise null. */
  apiKey: string | null;
  /** Mint (or rotate) a key and fill it in when no raw key is available. */
  onGenerate?: () => void;
  generating?: boolean;
  /** True when a "default" key already exists — the action rotates it. */
  rotate?: boolean;
}

type Lang = 'html' | 'react' | 'vue' | 'svelte' | 'angular' | 'ios' | 'android' | 'curl';
const TABS: { id: Lang; label: string }[] = [
  { id: 'html', label: 'HTML' },
  { id: 'react', label: 'React / Next' },
  { id: 'vue', label: 'Vue / Nuxt' },
  { id: 'svelte', label: 'Svelte' },
  { id: 'angular', label: 'Angular' },
  // Native, not a WebView: those run the browser SDK unchanged and belong on
  // the HTML tab. These two record the screen itself.
  { id: 'ios', label: 'iOS (Swift)' },
  { id: 'android', label: 'Android (Kotlin)' },
  { id: 'curl', label: 'cURL (test)' },
];

function testBatch(projectId: string) {
  return {
    v: 1,
    projectId,
    sessionId: '00000000-0000-4000-8000-000000000001',
    seq: 0,
    anonymousId: 'curl-test',
    events: [{ type: 2, timestamp: 1000, data: {} }],
    meta: {
      entryUrl: 'https://example.com/',
      currentUrl: 'https://example.com/',
      referrer: '',
      device: {
        browser: 'curl',
        browserVersion: '0',
        os: 'cli',
        osVersion: '0',
        deviceType: 'desktop',
        userAgent: 'curl',
      },
      screen: { width: 1440, height: 900, dpr: 1 },
      language: 'en',
      timezone: 'UTC',
      environment: 'production',
      sdkVersion: '0.1.0',
    },
    sentAt: 1700000000000,
  };
}

function snippet(lang: Lang, projectId: string, apiKey: string): string {
  const cfg = `projectId: '${projectId}',\n    apiKey: '${apiKey}',\n    endpoint: '${INGEST_ENDPOINT}',`;
  switch (lang) {
    case 'html':
      return `<script src="${SDK_URL}"></script>
<script>
  ReplayOps.init({
    ${cfg}
  });
</script>`;
    case 'react':
      return `import { useReplayOps } from '@replayops/sdk/react';

// In your root component (works with Next.js 'use client'):
useReplayOps({
    ${cfg}
});`;
    case 'vue':
      return `import { ReplayOpsPlugin } from '@replayops/sdk/vue';

app.use(ReplayOpsPlugin, {
    ${cfg}
});`;
    case 'svelte':
      return `import { onMount } from 'svelte';
import { Session } from '@replayops/sdk';

onMount(() => Session.init({
    ${cfg}
}));`;
    case 'angular':
      return `import { Session } from '@replayops/sdk';

// In AppComponent constructor or an APP_INITIALIZER:
Session.init({
    ${cfg}
});`;
    case 'ios':
      return `// Package.swift. Or Xcode ▸ File ▸ Add Package Dependencies…
// .package(url: "https://github.com/replayops/replayops", path: "mobile/ios")

import ReplayOps

// In AppDelegate.application(_:didFinishLaunchingWithOptions:)
// or the App's init(). Starting once is enough; it follows the app itself.
var config = ReplayOpsConfig(
  projectId: "${projectId}",
  apiKey: "${apiKey}",
  endpoint: "${INGEST_ENDPOINT}"
)
ReplayOps.start(config)

// As soon as you know who they are:
ReplayOps.identify(userId: "u_42", traits: ["plan": "pro"])`;
    case 'android':
      return `// build.gradle.kts
// implementation("ai.replayops:replayops-android:0.1.0")
// Needs <uses-permission android:name="android.permission.INTERNET" />

import ai.replayops.sdk.ReplayOps
import ai.replayops.sdk.ReplayOpsConfig

class App : Application() {
  override fun onCreate() {
    super.onCreate()
    ReplayOps.start(this, ReplayOpsConfig(
      projectId = "${projectId}",
      apiKey = "${apiKey}",
      endpoint = "${INGEST_ENDPOINT}",
    ))
    ReplayOps.identify("u_42", mapOf("plan" to "pro"))
  }
}`;
    case 'curl':
      return `curl -sS -X POST ${INGEST_ENDPOINT}/v1/collect \\
  -H 'content-type: application/json' \\
  -H 'x-api-key: ${apiKey}' \\
  -d '${JSON.stringify(testBatch(projectId))}'

# Expect: {"status":"accepted","accepted":1}`;
  }
}

/**
 * Copy-paste SDK install snippets for a project, pre-filled with the real
 * projectId and API key. When no raw key is available it offers to mint one
 * (raw keys are only shown once, so we never render a stale placeholder if the
 * caller can generate). The cURL tab can be executed in-place to confirm the
 * collector accepts the credentials.
 */
export function SdkInstall({ projectId, apiKey, onGenerate, generating, rotate }: Props) {
  const [tab, setTab] = useState<Lang>('html');
  const [copied, setCopied] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [testing, setTesting] = useState(false);

  // No raw key yet: prompt to generate one rather than showing a placeholder.
  if (!apiKey) {
    return (
      <Paper sx={{ p: 3, maxWidth: CARD_MAX_WIDTH }}>
        <Typography variant="h6" gutterBottom>
          Install the SDK
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {rotate
            ? "A “default” key already exists, but raw keys are shown only once. Rotate it to get a fresh, ready-to-paste snippet (the old “default” key is revoked)."
            : 'Generate an API key to get a ready-to-paste snippet pre-filled with your project ID and key. The raw key is shown only once.'}
        </Typography>
        <Button
          variant="contained"
          startIcon={generating ? <CircularProgress size={16} /> : <KeyIcon />}
          disabled={generating || !onGenerate}
          onClick={onGenerate}
        >
          {generating
            ? rotate
              ? 'Rotating…'
              : 'Generating…'
            : rotate
              ? 'Rotate “default” key & fill'
              : 'Generate API key & fill snippet'}
        </Button>
      </Paper>
    );
  }

  const code = snippet(tab, projectId, apiKey);

  const copy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(`${INGEST_ENDPOINT}/v1/collect`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'x-api-key': apiKey },
        body: JSON.stringify(testBatch(projectId)),
      });
      const text = await res.text();
      setTestResult({ ok: res.ok, text: `HTTP ${res.status} · ${text}` });
    } catch (err) {
      setTestResult({ ok: false, text: `Request failed: ${(err as Error).message}` });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Paper sx={{ p: 3, maxWidth: CARD_MAX_WIDTH }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">Install the SDK</Typography>
        <Button size="small" startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />} onClick={copy}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Pre-filled with this project's ID and API key. The ingest key is embedded
        in your client-side code, so it is public. Safe to paste into your site.
      </Typography>

      {/*
        Eight tabs, and the two native ones were the straw: at the default Tab
        width the first few scrolled out of sight, so the list of what the SDK
        supports was no longer readable at a glance. Which is most of what
        this row is for. Compact enough that all eight fit at a normal window
        width; still scrollable, because a phone cannot fit them at any size.
      */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          mb: 1,
          minHeight: 40,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontSize: 13.5,
            fontWeight: 600,
            minWidth: 'auto',
            minHeight: 40,
            px: 1.5,
          },
        }}
      >
        {TABS.map((t) => (
          <Tab key={t.id} value={t.id} label={t.label} />
        ))}
      </Tabs>
      <Divider sx={{ mb: 2 }} />

      <Box
        component="pre"
        sx={{
          bgcolor: '#0b0e14',
          color: '#e6edf3', // fixed light-on-dark; the block is always dark in both themes
          p: 2,
          borderRadius: 2,
          overflowX: 'auto',
          fontSize: 13,
          m: 0,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        }}
      >
        {code}
      </Box>

      {tab === 'curl' && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={testing ? <CircularProgress size={16} /> : <PlayArrowIcon />}
            onClick={runTest}
            disabled={testing}
          >
            Test ingestion
          </Button>
          {testResult && (
            <Alert severity={testResult.ok ? 'success' : 'error'} sx={{ py: 0, flex: 1 }}>
              {testResult.text}
            </Alert>
          )}
        </Stack>
      )}
    </Paper>
  );
}
